<template>
  <Card additional-classes="px-3" v-if="booking">
    <div class="p-3">
      <p class="heading text-primary-black">{{ t('payments.title') }}</p>
      <PaymentConditionsList :options="booking.options" :lihe-id="booking.liheId" :lihe-name="booking.liheName" :package-code="booking.packageCode" />
      <Totals
        :totalPrice="Number(booking.sellingPrice)"
        :leftToPay=booking.leftToPay
        :depositToPay="depositToPay"
        :totalPaid="totalPaid"
        :isQuoteOrCartInProgress="isQuoteOrCartInProgress"
      />
      <!-- Quote/cart: Réserver / Payer le solde live in ReservationSummary only -->
      <div
        v-if="!isQuoteOrCartInProgress"
        class="d-flex justify-content-end"
      >
        <ReserveActionsButton
          v-if="showPayBalanceButton"
          :booking="booking"
          :default-label="payActionLabel"
        />
        <PaymentButton v-else :user-id="userId" :booking="booking" />
      </div>
    </div>
  </Card>
</template>

<script lang="ts" setup>
  // Vue
  import { computed, ComputedRef, defineProps } from 'vue'

  // Types
  import { BookingSummary } from '@/services/BookingService.interface'

  // Composables
  import { useBookingStatus } from '@/composables/useBookingStatus'

  // Components
  import Card from '@/components/UI/Card.vue'
  import PaymentConditionsList from './PaymentConditionsList.vue'
  import Totals from './Totals.vue'
  import PaymentButton from '@/components/common/PaymentButton.vue'
  import ReserveActionsButton from '@/components/common/ReserveActionsButton.vue'
  import { useBookingFunnelActions } from '@/composables/useBookingFunnelActions'
  import { useBookingFunnelNavigation } from '@/composables/useBookingFunnelNavigation'

  // Store
  import { useStore } from '@/store'
  const store = useStore()

  // Translations
  import { useI18n } from 'vue-i18n'
  const { t } = useI18n()

  // Props
  interface Props {
    booking: BookingSummary | null
  }

  const props = defineProps<Props>()

  // Computed
  const userId: ComputedRef<string | null> = computed(
    () => store.state.auth?.user?.iresa_id || null
  )

  // Calculate the net paid amount
  const totalPaid: ComputedRef<number> = computed(() => {
    if (props.booking) {
      const { sellingPrice, leftToPay } = props.booking
      return sellingPrice - leftToPay
    }
    return 0
  })

  const depositToPay: ComputedRef<number | null> = computed(() => {
    if (!props.booking || props.booking.depositMet) return null

    const remainingDeposit = Number(props.booking.depositRemainingCents)
    if (Number.isFinite(remainingDeposit) && remainingDeposit > 0) {
      return remainingDeposit
    }

    const initialDeposit = Number(props.booking.initialPaymentRequired)
    if (Number.isFinite(initialDeposit) && initialDeposit > 0) {
      return initialDeposit
    }

    return null
  })

  const { isQuoteOrCartInProgress } = useBookingStatus(props.booking)

  const { showPayBalanceButton } = useBookingFunnelActions(
    () => props.booking as BookingSummary
  )
  const { payActionLabel } = useBookingFunnelNavigation(
    () => props.booking as BookingSummary
  )
</script>

<style scoped></style>
